import { useFormContext } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import getAddonGroupInfoBySettings, {
  SettingTranslationKey,
  SettingTranslationMeta,
  SettingsTranslatedTexts,
} from '@ancon/wildcat-utils/product/getAddonGroupInfoBySettings'

import useAppSelector from '../../../../store/hooks/useAppSelector'
import ProductConfigurationAccordion from '../../components/ProductConfigurationAccordion'
import { productsDetailsAddonGroupsSelector } from '../../store/productSelectors'
import { ConfigureProductFormState } from '../../types'
import { ProductConfigureAccordionId } from '../../constants'
import useShowProductValidationErrors from '../../hooks/useShowProductValidationErrors'
import getAddonGroupDisplayOrder from '../../utils/getAddonGroupDisplayOrder'
import isAddonGroupRequired from '../../utils/isAddonGroupRequired'

import CheckBoxTypeAddonGroup from './CheckBoxTypeAddonGroup'
import RadioButtonTypeAddonGroup from './RadioButtonTypeAddonGroup'
import QuantityModifierAddonGroup from './QuantityModifierAddonGroupForm'

export default function ProductAddonGroupsForm() {
  const { t } = useTranslation('common')

  const addonGroups = useAppSelector(productsDetailsAddonGroupsSelector)!

  const { formState, watch, setValue } =
    useFormContext<ConfigureProductFormState>()

  const { errors } = formState

  const showsValidationErrors = useShowProductValidationErrors()

  const [
    activeAccordionId,
    expandedAccordionIds = [],
    attentionAccordionId,
    selectedAddons,
  ] = watch([
    'activeAccordionId',
    'expandedAccordionIds',
    'attentionAccordionId',
    'selectedAddons',
  ])

  function addonGroupSettingTranslation({
    key,
    meta,
  }: {
    key: SettingTranslationKey
    meta?: SettingTranslationMeta
  }) {
    return t(`components.productDetailsModal.${key}`, meta) as string
  }

  const addonGroupSettingsTexts = Object.values(SettingTranslationKey).reduce(
    (acc, k) => {
      acc[k] = addonGroupSettingTranslation

      return acc
    },
    {} as SettingsTranslatedTexts,
  )

  function handleToggleAccordion(index: number, isExpanded: boolean) {
    const addonAccordionId = ProductConfigureAccordionId.AddonGroup(index)

    // Several groups can be open at once
    setValue(
      'expandedAccordionIds',
      isExpanded
        ? [...expandedAccordionIds, addonAccordionId]
        : expandedAccordionIds.filter(id => id !== addonAccordionId),
    )

    if (!isExpanded && activeAccordionId === addonAccordionId) {
      setValue('activeAccordionId', undefined)
    }
  }

  function getCounterText(index: number) {
    const { minimumAmount, maximumAmount } = addonGroups[index].addOnSettings
    const selectedCount = selectedAddons?.[index]?.selectedCount ?? 0

    if (isAddonGroupRequired(addonGroups[index].addOnSettings)) {
      return t('productModal.selectedOf', {
        count: selectedCount,
        total: maximumAmount || minimumAmount,
      })
    }

    return selectedCount
      ? t('productModal.selected', { count: selectedCount })
      : undefined
  }

  const displayOrder = getAddonGroupDisplayOrder(addonGroups)

  return displayOrder.map(i => {
    const {
      addOnGroupId,
      addOnGroupName,
      ingredients,
      products,
      addOnSettings,
    } = addonGroups[i]
    const { allowMultiple, maximumAmount, minimumAmount } = addOnSettings

    const shouldRenderQuantityButton = allowMultiple
    const shouldRenderRadioButton =
      !allowMultiple &&
      !!maximumAmount &&
      maximumAmount < 2 &&
      !!minimumAmount &&
      minimumAmount < 2
    const shouldRenderCheckBox =
      !shouldRenderRadioButton && !shouldRenderQuantityButton

    const accordionId = ProductConfigureAccordionId.AddonGroup(i)

    const addonGroupError = showsValidationErrors
      ? errors?.selectedAddons?.[i]
      : undefined
    const hasErrors = !!addonGroupError
    const errorMessage = addonGroupError?.message

    const isRequired = isAddonGroupRequired(addOnSettings)
    const isComplete =
      isRequired &&
      (selectedAddons?.[i]?.selectedCount ?? 0) >=
        (addOnSettings.minimumAmount ?? 0) &&
      !errors?.selectedAddons?.[i]

    const { translation: addonSettingsInfo } = getAddonGroupInfoBySettings(
      addOnSettings,
      addonGroupSettingsTexts,
    )

    const hasBlockedProduct =
      products?.some(product => product.isBlocked) ?? false

    const title = addOnSettings.customerFacingName || addOnGroupName

    return (
      <ProductConfigurationAccordion
        key={addOnGroupId}
        title={title}
        sectionId={accordionId}
        subtitle={errorMessage}
        additionalInfo={addonSettingsInfo}
        hasError={hasErrors}
        expanded={
          activeAccordionId === accordionId ||
          expandedAccordionIds.includes(accordionId)
        }
        isRequired={isRequired}
        counterText={getCounterText(i)}
        isComplete={isComplete}
        isAttention={attentionAccordionId === accordionId}
        onToggle={isExpanded => handleToggleAccordion(i, isExpanded)}
        maxHeight={9999}
      >
        {shouldRenderCheckBox && (
          <CheckBoxTypeAddonGroup
            ingredients={ingredients || []}
            products={products || []}
            addOnIndex={i}
          />
        )}
        {shouldRenderRadioButton && (
          <RadioButtonTypeAddonGroup
            ingredients={ingredients || []}
            products={products || []}
            addOnIndex={i}
          />
        )}
        {shouldRenderQuantityButton && (
          <QuantityModifierAddonGroup
            hasBlockedProduct={hasBlockedProduct}
            ingredients={ingredients || []}
            products={products || []}
            addOnIndex={i}
          />
        )}
      </ProductConfigurationAccordion>
    )
  })
}
